import type { ICampaignWorkspaceData, WorkspacePerspective } from '../redux/buzzbridge/campaignWorkspace.types';
import { CAMPAIGN_ACCESS_DENIED_CODES, CAMPAIGN_ACCESS_DENIED_HTTP_STATUS } from '../constants/campaignAccessDenied';

/** Resolve a Mongo id from a string or `{ _id }` stub. */
export function resolveWorkspaceEntityId(value: unknown): string | null {
    if (value == null) return null;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed || null;
    }
    if (typeof value === 'object' && value !== null && '_id' in value) {
        const id = (value as { _id?: unknown })._id;
        if (typeof id === 'string' && id.trim()) return id.trim();
        if (id != null && typeof id !== 'object') return String(id);
    }
    return null;
}

export function isInternalCampaignTeamRole(role: string | null | undefined): boolean {
    return role === 'admin' || role === 'super-admin';
}

export function isBrandCampaignTeamSenderRole(role: string | null | undefined): boolean {
    return role === 'owner' || role === 'admin' || role === 'editor';
}

export function isBrandCampaignTeamReaderRole(role: string | null | undefined): boolean {
    return isBrandCampaignTeamSenderRole(role) || role === 'viewer';
}

function workspaceTeamRole(workspace: ICampaignWorkspaceData | null | undefined): string | null | undefined {
    const access = workspace?.campaign?.access;
    if (!access || typeof access !== 'object') return undefined;
    return typeof access.role === 'string' ? access.role : null;
}

export function isCampaignWorkspaceHost(
    workspace: ICampaignWorkspaceData | null | undefined,
    currentUserId: string | null | undefined,
): boolean {
    if (!workspace || !currentUserId?.trim()) return false;
    const hostId = resolveWorkspaceEntityId(workspace.campaign?.created_by);
    return Boolean(hostId && hostId === currentUserId.trim());
}

export function isCampaignWorkspaceInfluencer(
    workspace: ICampaignWorkspaceData | null | undefined,
    options: {
        currentUserId?: string | null;
        currentInfluencerId?: string | null;
    },
): boolean {
    if (!workspace) return false;
    const { currentUserId, currentInfluencerId } = options;

    const participantInfluencerId =
        resolveWorkspaceEntityId(workspace.creator?.influencer) ??
        resolveWorkspaceEntityId(workspace.participant?.influencer);
    if (currentInfluencerId?.trim() && participantInfluencerId) {
        if (currentInfluencerId.trim() === participantInfluencerId) return true;
    }

    const creatorUserId = resolveWorkspaceEntityId(workspace.creator?.user);
    if (currentUserId?.trim() && creatorUserId) {
        if (currentUserId.trim() === creatorUserId) return true;
    }

    return false;
}

/**
 * Mirrors backend brand-workspace / creator-workspace GET authorization.
 */
export function canViewCampaignWorkspace(
    workspace: ICampaignWorkspaceData | null | undefined,
    options: {
        perspective: WorkspacePerspective;
        currentUserId?: string | null;
        currentUserRole?: string | null;
        currentInfluencerId?: string | null;
    },
): boolean {
    if (!workspace) return false;
    if (isInternalCampaignTeamRole(options.currentUserRole)) return true;
    if (options.perspective === 'brand') {
        if (isCampaignWorkspaceHost(workspace, options.currentUserId)) return true;
        return isBrandCampaignTeamReaderRole(workspaceTeamRole(workspace));
    }
    return isCampaignWorkspaceInfluencer(workspace, {
        currentUserId: options.currentUserId,
        currentInfluencerId: options.currentInfluencerId,
    });
}

/**
 * Mirrors backend `assertCanAccessThread` for send/list/patch.
 * Host, influencer on the row, or internal admin may message.
 */
export function canSendCampaignThreadMessage(
    workspace: ICampaignWorkspaceData | null | undefined,
    options: {
        currentUserId?: string | null;
        currentUserRole?: string | null;
        currentInfluencerId?: string | null;
    },
): boolean {
    if (!workspace) return false;
    if (isInternalCampaignTeamRole(options.currentUserRole)) return true;
    if (isCampaignWorkspaceHost(workspace, options.currentUserId)) return true;
    if (isBrandCampaignTeamSenderRole(workspaceTeamRole(workspace))) return true;
    return isCampaignWorkspaceInfluencer(workspace, {
        currentUserId: options.currentUserId,
        currentInfluencerId: options.currentInfluencerId,
    });
}

function getCampaignAccessDeniedCode(error: unknown): string | undefined {
    const err = error as {
        response?: { data?: { details?: { code?: string }; code?: string } };
    };
    return err?.response?.data?.details?.code ?? err?.response?.data?.code;
}

/**
 * True for workspace/thread membership denials from the API.
 * Accepts temporary **409** stand-in and legacy **403**; also matches machine codes.
 * Does **not** treat bare 401 as workspace-forbidden (session handling).
 */
export function isCampaignWorkspaceAuthError(error: unknown): boolean {
    const err = error as {
        response?: { status?: number; data?: { message?: string; details?: { code?: string }; code?: string } };
        status?: number;
        message?: string;
    };
    const code = getCampaignAccessDeniedCode(error);
    if (code === CAMPAIGN_ACCESS_DENIED_CODES.WORKSPACE || code === CAMPAIGN_ACCESS_DENIED_CODES.THREAD) {
        return true;
    }

    const status = err?.response?.status ?? err?.status;
    const message = String(err?.response?.data?.message ?? err?.message ?? '');
    const looksLikeMembershipMessage = /not authorized/i.test(message);

    if (status === CAMPAIGN_ACCESS_DENIED_HTTP_STATUS && looksLikeMembershipMessage) {
        return true;
    }
    // Legacy / direct-origin responses before CF workaround
    if (status === 403 && looksLikeMembershipMessage) {
        return true;
    }
    return false;
}
